import React from 'react';
import { Navigate, Outlet, useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { MainLayout, type AppSection, type FocusPanelNavItem } from '@hofsync/design-system';
import { LayoutDashboard, LayoutGrid, Tractor, FileText, Clock, Shield, ClipboardCheck, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import { useToast } from '../contexts/ToastContext';
import { SearchProvider, useSearch } from '../contexts/SearchContext';
import Footer from '../components/Footer';
import ImpersonationBanner from '../components/ImpersonationBanner';
import { ChatProvider } from '../components/chat/ChatProvider';
import { AppSearchController } from '../components/search/AppSearchController';
import { hasAccessibleApp } from '../pages/apps/registry';
import { LABOUR_PATH, isLabourAccessEnded, isSelfOnlyLabourSeat } from '../utils/landing';
import { resolveSectionNavigate } from './resolveSectionNavigate';
import { headerName } from './headerName';

/**
 * Map route paths to MainLayout app sections
 */
const routeToSection: Record<string, AppSection> = {
  '/pruefstatus': 'pruefstatus',
  '/documents': 'documents',
  '/settings': 'documents',
  '/connections': 'documents',
  '/integrations': 'documents',
  '/admin': 'admin',
  '/oauth/callback': 'documents',
  '/workspace': 'workspace',
  '/farm': 'my-farm',
  '/history': 'history',
  '/apps': 'apps',
  '/agent': 'agent',
};

/**
 * Derive the active section from the current route path
 */
function getActiveSection(pathname: string): AppSection {
  // Check exact matches first
  if (routeToSection[pathname]) {
    return routeToSection[pathname];
  }

  // Check prefix matches for dynamic routes like /oauth/callback/:provider
  for (const [route, section] of Object.entries(routeToSection)) {
    if (pathname.startsWith(route)) {
      return section;
    }
  }

  // Default to documents
  return 'documents';
}

/**
 * Inner layout component that uses SearchContext
 */
const ProtectedLayoutInner: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, userPhone, userRoles, userProfile, impersonation, stopImpersonation, logout } = useAuth();
  const { hasAnyFlag, hasFlag } = useFeatureFlags();
  const { showToast } = useToast();
  const { searchQuery, setSearchQuery } = useSearch();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user has admin role — hide admin nav when impersonating
  const isAdmin = userRoles.includes('admin') && !impersonation?.active;

  // Feature-flag-based nav visibility
  const showPruefstatus = hasAnyFlag(['mock_ui.pruefstatus', 'twin.pruefstatus']);
  const showWorkspace = hasAnyFlag(['mock_ui.workspace', 'twin.events']);
  const showFarm = hasAnyFlag(['mock_ui.farm', 'twin.entities']);
  // Subtask 06 (audit-logs unification): /history is a real protected
  // route now, backed by the unified audit-events read API. The
  // `mock_ui.history` flag is retired with the mock data path.
  const showHistory = true;
  // Show the marketplace menu item iff at least one available app is
  // enabled for this user. Registry is the source of truth — new apps
  // appear in the menu automatically once their flag is granted.
  // HOF-696 D6: a farm app (active for the farm × the seat's permission) counts too.
  const showApps = hasAccessibleApp(hasFlag, userProfile?.active_farm);

  // Build localized nav items for FocusPanel. The KI-Assistent leads the
  // rail (FR-015 follow-up): it is the cross-cutting entry point, ahead of
  // the flag-gated sections.
  const navItems: FocusPanelNavItem[] = [
    { id: 'agent', name: t('focusPanel.agent'), icon: <Sparkles className="focus-panel__icon" /> },
    ...(showPruefstatus ? [{ id: 'pruefstatus', name: t('focusPanel.pruefstatus'), icon: <ClipboardCheck className="focus-panel__icon" /> }] : []),
    ...(showWorkspace ? [{ id: 'workspace', name: t('focusPanel.workspace'), icon: <LayoutDashboard className="focus-panel__icon" /> }] : []),
    ...(showApps ? [{ id: 'apps', name: t('focusPanel.apps'), icon: <LayoutGrid className="focus-panel__icon" /> }] : []),
    ...(showFarm ? [{ id: 'my-farm', name: t('focusPanel.myFarm'), icon: <Tractor className="focus-panel__icon" /> }] : []),
    { id: 'documents', name: t('focusPanel.documents'), icon: <FileText className="focus-panel__icon" /> },
    ...(showHistory ? [{ id: 'history', name: t('focusPanel.history'), icon: <Clock className="focus-panel__icon" /> }] : []),
    ...(isAdmin ? [{ id: 'admin', name: t('focusPanel.admin'), icon: <Shield className="focus-panel__icon" /> }] : []),
  ];

  // #951: clicking the active section drops `?lense=…`/`?category=…` and
  // resets to the bare route — which since #2108 lands directly in the
  // section's default lens (no starting page). See resolveSectionNavigate
  // for the three-branch matrix.
  const handleSectionChange = (section: AppSection) => {
    const result = resolveSectionNavigate(section, location.pathname, location.search);
    if (!result) return;
    if (result.options) {
      navigate(result.target, result.options);
      return;
    }
    navigate(result.target);
  };

  const handleLogout = async () => {
    try {
      await logout();
      showToast(t('toast.logoutSuccess'), 'success');
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      showToast(t('common.error'), 'error');
    }
  };

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  const handleIntegrationsClick = () => {
    navigate('/integrations');
  };

  // Show loading state
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        color: '#6c757d'
      }}>
        {t('common.loading')}
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // HOF-696 D5: a self-only labour seat holds no farm.read, so every page of this
  // shell would answer 403 — its one page is the labour app, in its own shell. So is a
  // worker whose access ended (D12): no seat at all, and the labour app tells them why.
  if (isSelfOnlyLabourSeat(userProfile?.active_farm) || isLabourAccessEnded(userProfile)) {
    return <Navigate to={LABOUR_PATH} replace />;
  }

  const activeSection = getActiveSection(location.pathname);

  // Build user info for Header — the impersonated farm, else the farm being worked in (HOF-627)
  const userInfo = { name: headerName(userProfile, impersonation, userPhone) };

  // Get mobile page title from active section's nav item
  const activeNavItem = navItems.find((item) => item.id === activeSection);
  const mobilePageTitle = activeNavItem?.name || t('focusPanel.documents');

  return (
    <div className={impersonation?.active ? 'impersonation-active' : ''}>
    {impersonation?.active && (
      <ImpersonationBanner
        farmName={impersonation.farm_name}
        userId={impersonation.user_id}
        onExit={stopImpersonation}
      />
    )}
    <ChatProvider>
      {/* #1392 — app-wide Cmd/Ctrl+F search overlay. Mounted here (inside
          SearchProvider) so the shortcut + results dropdown are available
          on every protected route; fully self-contained — no edits to the
          gutschrift-check canvas. */}
      <AppSearchController />
      <MainLayout
        activeSection={activeSection}
        onSectionChange={handleSectionChange}
        user={userInfo}
        logoSrc="/hofsync_logo.gif"
        logoHref="/"
        focusPanelNavItems={navItems}
        showSearch={true}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder={t('header.searchPlaceholder')}
        searchCollapsedLabel={t('header.searchCollapsed')}
        onLogout={handleLogout}
        // HOF-708: while impersonating too — the menu offers what the viewed person has; each page
        // shows the viewed farm and the backend refuses what an impersonation may not do.
        onSettingsClick={handleSettingsClick}
        onIntegrationsClick={handleIntegrationsClick}
        focusPanelExpanded={false}
        settingsLabel={t('header.settings')}
        integrationsLabel={t('header.integrations')}
        signOutLabel={t('header.signOut')}
        mobilePageTitle={mobilePageTitle}
        footer={<Footer />}
      >
        {children ?? <Outlet />}
      </MainLayout>
    </ChatProvider>
    </div>
  );
};

/**
 * ProtectedLayout wraps protected routes with MainLayout and SearchContext.
 * It handles authentication checks and provides the MainLayout chrome.
 */
const ProtectedLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  return (
    <SearchProvider>
      <ProtectedLayoutInner>{children}</ProtectedLayoutInner>
    </SearchProvider>
  );
};

export default ProtectedLayout;
